import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';

/**
 * Lightweight focus-trap + Escape-to-close for our hand-rolled modal /
 * slide-over panels. We have no @angular/cdk dependency, so this stands
 * in for cdkTrapFocus + the overlay's keydown handling.
 *
 * Apply to the dialog *container* (the panel box, NOT the backdrop) and
 * give that element role="dialog" + aria-modal="true" + an aria-label:
 *
 *   <aside appModalDialog (dialogClose)="close()"
 *          role="dialog" aria-modal="true" aria-label="…">
 *
 * Behaviour:
 *   - on init: remember whatever had focus, then move focus into the
 *     dialog (first focusable child, or the host itself).
 *   - Tab / Shift+Tab cycle only within the host's focusables, wrapping
 *     at both ends so focus never escapes behind the overlay.
 *   - Escape emits (dialogClose) — the host wires it to its close method.
 *   - on destroy: restore focus to the previously-focused element.
 */
@Directive({
  selector: '[appModalDialog]',
  standalone: true,
})
export class ModalDialogDirective implements OnInit, OnDestroy {
  /** Emitted when the user presses Escape inside the dialog. */
  @Output() dialogClose = new EventEmitter<void>();

  /** The element that held focus before the dialog opened — restored on destroy. */
  private previouslyFocused: HTMLElement | null = null;

  // Matches the elements a user can Tab to. Excludes tabindex="-1"
  // (programmatically-focusable only) but keeps the host-fallback case.
  private static readonly FOCUSABLE =
    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

  constructor(private host: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    // Stash the trigger so we can hand focus back when the dialog closes.
    this.previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Defer one tick so the panel's content has rendered before we
    // hunt for something to focus.
    setTimeout(() => {
      const focusables = this.focusable();
      if (focusables.length) {
        focusables[0].focus();
      } else {
        // No focusable children — make the host itself focusable so the
        // dialog still captures focus (and screen readers land on it).
        const el = this.host.nativeElement;
        el.setAttribute('tabindex', '-1');
        el.focus();
      }
    }, 0);
  }

  ngOnDestroy(): void {
    // Restore focus to the trigger if it's still in the document.
    if (this.previouslyFocused && document.contains(this.previouslyFocused)) {
      this.previouslyFocused.focus();
    }
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.dialogClose.emit();
      return;
    }

    if (event.key !== 'Tab') return;

    // Trap Tab within the dialog, wrapping at both ends.
    const focusables = this.focusable();
    if (!focusables.length) {
      // Nothing to cycle through — keep focus pinned to the host.
      event.preventDefault();
      this.host.nativeElement.focus();
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (event.shiftKey) {
      // Shift+Tab off the first element wraps to the last.
      if (active === first || !this.host.nativeElement.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else {
      // Tab off the last element wraps to the first.
      if (active === last || !this.host.nativeElement.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  /** Current visible, focusable descendants of the host, in DOM order. */
  private focusable(): HTMLElement[] {
    const nodes = this.host.nativeElement.querySelectorAll<HTMLElement>(
      ModalDialogDirective.FOCUSABLE
    );
    // Skip elements that are hidden (display:none / detached) — they
    // can't actually receive focus and would create dead Tab stops.
    return Array.from(nodes).filter(
      el => el.offsetParent !== null || el === document.activeElement
    );
  }
}
